import React, { useState, useRef, useEffect } from "react";
import { Stack } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { useDebounce } from "../../hooks";

import { HubHookFormInput } from "../HubHookFormInput";
import { InputLabel } from "../InputLabel";
import HubHookFormSearchResults from "./HubHookFormSearchResults";
import { StyledRenderValue } from "./styles";

export const HubHookFormSearchField = ({
  name = "search",
  label = "Search",
  placeholder = "Search...",
  onChange = () => {},
  options = [],
  value,
  setValue = () => {},
  required = true,
  defaultValue,
  disabled,
  optionIdKey = "id",
  RenderOption,
  sx = {},
  debounceTime = 500,
  ...rest
}) => {
  const { setValue: setFormValue, watch } = useFormContext();
  const selected = watch(name);
  const selectedOption = options?.find((op) => op[optionIdKey] === selected);

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(selectedOption?.label);
  const stackRef = useRef();

  useEffect(() => {
    !RenderOption && setInputValue(selectedOption?.label);
  }, [selectedOption]);

  const handleClickOutside = (event) => {
    if (stackRef.current && !stackRef.current.contains(event.target)) {
      const label = selectedOption?.label;
      !RenderOption && setInputValue(label);
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const newValue = e?.target?.value;
    setInputValue(newValue);
    debouncedSetValue(newValue);
  };

  const handleChangeDebounced = (newValue) => {
    setValue(newValue);
  };

  const onClose = () => {
    setIsFocused(false);
  };

  const onSelect = (option) => {
    onChange(option);
    setFormValue(name, option[optionIdKey]);
    setInputValue(RenderOption ? "" : option.label);
    onClose();
  };

  const onFocus = () => {
    RenderOption && setInputValue("");
    setIsFocused(true);
  };

  const debouncedSetValue = useDebounce(handleChangeDebounced, debounceTime);

  const showResults = isFocused && value;
  const width = stackRef?.current?.offsetWidth || 250;
  const showRender = !isFocused && RenderOption && selectedOption;

  return (
    <Stack
      ref={stackRef}
      className="HubHookSearchField-stack"
      sx={{ width: "100%", ...sx }}
    >
      <HubHookFormInput
        name={name}
        required={required}
        defaultValue={defaultValue}
        hidden
      />
      {label && (
        <InputLabel
          label={label}
          required={required}
          id={`HubHookField-${name}-display`}
        />
      )}
      <Stack
        sx={{ width: "100%", position: "relative" }}
        className="HubSearch-Stack"
      >
        <HubHookFormInput
          name={`${name}-display`}
          placeholder={selectedOption ? "" : placeholder}
          onChange={handleChange}
          onFocus={onFocus}
          value={inputValue}
          disableHelperText
          {...rest}
        />
        {showRender && (
          <StyledRenderValue onClick={onFocus} className="HubSearch-Render">
            <RenderOption {...selectedOption} />
          </StyledRenderValue>
        )}
      </Stack>
      {showResults && (
        <HubHookFormSearchResults
          width={width}
          options={options}
          onSelect={onSelect}
          RenderOption={RenderOption}
        />
      )}
    </Stack>
  );
};

export default HubHookFormSearchField;
